import { useState } from "react";
import { Availability, State, TimeSlot } from "../../types";
import { AvailabilitySearchResponseToTimeSlotList } from "../../types/TimeSlot";
import { AvailabilitySearchResponse } from "../../domain_objects/AvailabilitySearchResponse";
import DateHelper from "../../helpers/DateHelper";
import {
  Button,
  DatePicker,
  Dropdown,
  DropdownOptions,
  Logo,
  TimeSelector,
} from "../../components/";
import { defaultThemeStyles } from "../../mocks";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ClockSolid, UserSolid } from "../../assets/icons";
import WidgetService from "../../services/WidgetService";
import mockWidgetService from "../../mocks/mockWidgetService";
import { AvailabilityType } from "../../enums/AvailbilityType";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 10px;
`;

const StyledDiv = styled.div<{
  styles: { OverallGeneralTextColour: string };
}>`
  margin-bottom: 15px;
  font-size: 13px;
  color: ${({ styles }) => styles.OverallGeneralTextColour};
`;

export type SelectDatePageProps = {
  state: State;
  nextPage: (payload: Partial<State>) => void;
  timeSlotOptions: DropdownOptions;
  widgetService: WidgetService | mockWidgetService;
};

export const SelectDatePage: React.FC<SelectDatePageProps> = ({
  nextPage,
  state,
  timeSlotOptions,
  widgetService,
}) => {
  const { bookingDetails } = state;
  const { t } = useTranslation();
  const [partySize, setPartySize] = useState(
    bookingDetails?.partySize
      ? bookingDetails?.partySize
      : state.crossSellSetup?.OnlinePartySizeDefault || 2
  );
  const [selectedDate, setSelectedDate] = useState(
    bookingDetails?.selectedDate ?? new Date()
  );
  const [timeSlot, setTimeSlot] = useState<TimeSlot>(state.initialTimeSlot);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const completePage = async () => {
    if (!timeSlot.TimeSlot.TimeSlot.length) {
      setErrorMessage(t("ValidTime"));
      return;
    }
    setIsLoading(true);
    await widgetService
      .availabilitySearch(
        state.hostname || "",
        state.micrositeName || "",
        selectedDate,
        partySize,
        state.channelCode || "",
        AvailabilityType.Reservation
      )
      .then((response) => {
        setIsLoading(false);

        if (state.crossSellSetup!.UseNewSkipTimeSelectFlow) {
          let availabilityType: Availability | null = null;
          let selectedTimeSlot: TimeSlot | undefined;
          const availabilityResponses: Array<AvailabilitySearchResponse> = [
            {
              ...response,
              MicrositeName: state.micrositeName!,
            },
          ];
          const baseAvailabilitySearch = availabilityResponses.find(
            (a) => a.MicrositeName === state.micrositeName
          );

          if (baseAvailabilitySearch) {
            const timeslots = DateHelper.getTwoHourTimeSlots(
              AvailabilitySearchResponseToTimeSlotList(baseAvailabilitySearch),
              timeSlot
            );
            if (timeslots.length === 0) {
              availabilityType = "partialOutsideTwoHours";
            }

            const slicedTimeSlots = timeslots.map((t) =>
              DateHelper.sliceTimeSlot(t)
            );
            const slicedInitialTimeSlot = DateHelper.sliceTimeSlot(timeSlot);
            if (
              timeslots.length > 0 &&
              !slicedTimeSlots.includes(slicedInitialTimeSlot)
            ) {
              availabilityType = "partialWithinTwoHours";
            }

            if (
              timeslots.length > 0 &&
              slicedTimeSlots.includes(slicedInitialTimeSlot)
            ) {
              selectedTimeSlot = timeslots.find((t) => {
                return (
                  DateHelper.sliceTimeSlot(t) ===
                  DateHelper.sliceTimeSlot(timeSlot)
                );
              });

              availabilityType = "full";
            }
          }

          nextPage({
            ...state,
            bookingDetails: {
              partySize,
              selectedDate,
              ...(availabilityType === "full" &&
                selectedTimeSlot && { timeSlot: selectedTimeSlot }),
            },
            initialTimeSlot: timeSlot,
            availabilityResponses: [
              {
                ...response,
                MicrositeName: state.micrositeName!,
              },
            ],
            availabilityType,
          });
        }

        if (!state.crossSellSetup!.UseNewSkipTimeSelectFlow) {
          nextPage({
            ...state,
            bookingDetails: {
              partySize,
              selectedDate,
            },
            initialTimeSlot: timeSlot,
            availabilityResponses: [
              {
                ...response,
                MicrositeName: state.micrositeName!,
              },
            ],
          });
        }
      });
  };

  const getPartySizeOptions = (): DropdownOptions => {
    const minPartySize = state.crossSellSetup?.MinOnlinePartySize || 2;
    const maxPartySize = state.crossSellSetup?.MaxOnlinePartySize || 8;

    const partySizeOptions: DropdownOptions = [];
    for (let i = minPartySize; i <= maxPartySize; i++) {
      partySizeOptions.push({ label: i.toString(), value: i });
    }

    return partySizeOptions;
  };

  return (
    <StyledPageContainer data-id="select-date-page-container">
      <Logo styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      {!isLoading && (
        <>
          <Dropdown
            options={getPartySizeOptions()}
            onSelect={(option) => setPartySize(Number(option))}
            label={t("PartySize")}
            selected={partySize}
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            icon={UserSolid}
            dataId="party-size-dropdown"
          />
          <DatePicker
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          />
          {state.crossSellSetup!.UseNewSkipTimeSelectFlow ? (
            <TimeSelector
              selectedTimeSlot={DateHelper.sliceTimeSlot(timeSlot)}
              setTimeSlot={(t) =>
                setTimeSlot({ TimeSlot: { TimeSlot: t } } as TimeSlot)
              }
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              errorMessage={errorMessage}
            />
          ) : (
            <>
              <Dropdown
                options={timeSlotOptions}
                onSelect={(t) =>
                  setTimeSlot({ TimeSlot: { TimeSlot: t } } as TimeSlot)
                }
                label={t("Time")}
                selected={DateHelper.sliceTimeSlot(timeSlot)}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                icon={ClockSolid}
                dataId="time-slot-dropdown"
              />
              <StyledDiv
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                data-id="choose-more-specific-time-slots"
              >
                {t("ChooseMoreSpecificTimeSlotsAtNextStep")}
              </StyledDiv>
            </>
          )}
          <Button
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            onClick={completePage}
            dataId="check-availability-button"
          >
            {t("CheckAvailability")}
          </Button>
        </>
      )}
      {isLoading && <LoadingSpinner hostname={state.hostname || ""} />}
    </StyledPageContainer>
  );
};

export default SelectDatePage;
